import { createSlice } from '@reduxjs/toolkit'
import API, { API_VERSION, REQUEST_METHOD } from '../../../api'
import { OVERRIDE_COLUMNS_BY_SNAKE_KEY } from '../../../pages/feeManagement/constants/Columns'
import { OVERRIDE_TYPES, API_OVERRIDE_TYPE_MAPPING } from '../../../pages/feeManagement/constants/OverrideTypes'
import { createTrackedAsyncThunk } from '../../../pages/feeManagement/utils/reduxHelpers'

export const initialState = {
  overrideSheets: {
    items: [],
    isLoading: false,
    error: null,
  },
  overrideData: {
    data: null,
    isLoading: false,
    error: null,
  },
  createOverride: {
    isLoading: false,
    error: null,
    success: false,
    createdSheetId: null,
    result: null,
  },
  mappingData: {
    data: {
      products: [],
      panelists: [],
      state_and_counties_map: {},
      branches: [],
    },
    isLoading: false,
    error: null,
  },
  updateOverride: {
    isLoading: false,
    error: null,
    success: false,
  },
  updateOverrides: {
    isLoading: false,
    error: null,
    success: false,
    result: null,
  },
  overrideLookup: {
    data: null,
    isLoading: false,
    error: null,
  },
}

// Helper function to determine override type from entity_types
const getOverrideType = (entityTypes) => {
  const sortedTypes = [...entityTypes].sort()
  const typeString = sortedTypes.join(',')

  if (typeString === API_OVERRIDE_TYPE_MAPPING.VENDOR_LOCATION.sort().join(',')) {
    return OVERRIDE_TYPES.VENDOR_LOCATION.value
  } else if (typeString === API_OVERRIDE_TYPE_MAPPING.VENDOR.sort().join(',')) {
    return OVERRIDE_TYPES.VENDOR.value
  } else if (typeString === API_OVERRIDE_TYPE_MAPPING.LOCATION.sort().join(',')) {
    return OVERRIDE_TYPES.LOCATION.value
  } else if (typeString === API_OVERRIDE_TYPE_MAPPING.BRANCH_VENDOR_LOCATION.sort().join(',')) {
    return OVERRIDE_TYPES.BRANCH_VENDOR_LOCATION.value
  } else if (typeString === API_OVERRIDE_TYPE_MAPPING.BRANCH_VENDOR.sort().join(',')) {
    return OVERRIDE_TYPES.BRANCH_VENDOR.value
  } else if (typeString === API_OVERRIDE_TYPE_MAPPING.BRANCH_LOCATION.sort().join(',')) {
    return OVERRIDE_TYPES.BRANCH_LOCATION.value
  } else if (typeString === API_OVERRIDE_TYPE_MAPPING.BRANCH.sort().join(',')) {
    return OVERRIDE_TYPES.BRANCH.value
  }
  return null
}

export const fetchOverrideSheets = createTrackedAsyncThunk(
  'feeManagement/fetchOverrideSheets',
  async (_, { rejectWithValue }) => {
    try {
      const response = await API('overrides/sheets', API_VERSION.TWO).request(REQUEST_METHOD.GET)

      // Transform the response to include derived override type
      const transformedSheets = response.sheets.map((sheet) => {
        const overrideType = getOverrideType(sheet.entity_types)
        const transformed = {
          id: sheet.id,
          title: sheet.description,
          status: sheet.active ? 'active' : 'inactive',
          overrideType,
          overriddenFields: sheet.data_types.map((type) => OVERRIDE_COLUMNS_BY_SNAKE_KEY[type]?.columnKey || type),
          created: new Date(sheet.created).toLocaleDateString(),
          createdBy: sheet.created_by,
          lenderId: sheet.lender_id,
          // Store the original entity_types for reference
          entityTypes: sheet.entity_types,
        }
        return transformed
      })

      return transformedSheets
    } catch (err) {
      return rejectWithValue(err.errors?._error || 'Failed to fetch override sheets')
    }
  },
  { errorSource: 'feeManagement.overrideSheets' },
)

export const fetchOverrideData = createTrackedAsyncThunk(
  'feeManagement/fetchOverrideData',
  async (sheetId, { rejectWithValue }) => {
    try {
      const api = API(`overrides/sheets/${sheetId}/overrides.json`, API_VERSION.TWO)
      const response = await api.request(REQUEST_METHOD.GET, {}, {}, true)
      return response
    } catch (err) {
      return rejectWithValue(err.errors?._error || 'Failed to fetch override data')
    }
  },
  { errorSource: 'feeManagement.overrideData' },
)

export const updateOverrideSheet = createTrackedAsyncThunk(
  'feeManagement/updateOverrideSheet',
  async (payload, { rejectWithValue }) => {
    try {
      const { id, ...rest } = payload
      const response = await API(`overrides/sheets/${id}`, API_VERSION.TWO).request(REQUEST_METHOD.PATCH, {}, rest)
      return response
    } catch (err) {
      return rejectWithValue(err.errors?._error || 'Failed to update override sheet')
    }
  },
  { errorSource: 'feeManagement.updateOverride' },
)

export const updateOverrides = createTrackedAsyncThunk(
  'feeManagement/updateOverrides',
  async (payload, { rejectWithValue }) => {
    try {
      const { sheetId, overrides } = payload
      // PUT bulk-upserts the sheet's rows: rows with _id update in place, rows
      // without _id insert, and rows marked { deleted: true } are soft-deleted.
      // Route is registered without a trailing slash, matching the other
      // overrides endpoints in this file.
      const response = await API(`overrides/sheets/${sheetId}/overrides`, API_VERSION.TWO).request(
        REQUEST_METHOD.PUT,
        {},
        { overrides },
      )
      return response
    } catch (err) {
      // request() rejects with the parsed error object directly (keys mapped from
      // the JSON body). On validation failure the backend returns 400 with an
      // `error` summary and invalid_overrides (a JSON string of [{ override,
      // errors }]). Fold the first few row errors into the message so the toast
      // says WHAT failed instead of a generic fallback — the whole batch rolls
      // back, so the user needs enough detail to find the offending row.
      let invalidOverrides = []
      try {
        invalidOverrides = JSON.parse(err?.invalid_overrides || '[]')
      } catch {
        invalidOverrides = []
      }
      const rowErrors = [...new Set(invalidOverrides.flatMap((item) => item?.errors || []))]
      const shownErrors = rowErrors.slice(0, 3).join(' ')
      const remaining = rowErrors.length - 3
      const detail = shownErrors ? ` ${shownErrors}${remaining > 0 ? ` (and ${remaining} more)` : ''}` : ''
      return rejectWithValue({
        message: `${err?._error || err?.error || 'Failed to update overrides.'}${detail}`,
        invalidOverrides,
      })
    }
  },
  {
    errorSource: 'feeManagement.updateOverrides',
    customErrorContext: (payload) => ({
      sheetId: payload?.sheetId,
      overridesCount: payload?.overrides?.length || 0,
    }),
  },
)

export const createOverrideSheet = createTrackedAsyncThunk(
  'feeManagement/createOverrideSheet',
  async (payload, { rejectWithValue }) => {
    try {
      const { description, entityTypes, dataTypes, overrides, active = false } = payload

      const requestBody = {
        description,
        entity_types: entityTypes,
        data_types: dataTypes,
        active,
        overrides,
      }

      const response = await API('overrides/sheets', API_VERSION.TWO).request(REQUEST_METHOD.POST, {}, requestBody)

      return response
    } catch (err) {
      return rejectWithValue(err.errors?._error || 'Failed to create override sheet')
    }
  },
  {
    errorSource: 'feeManagement.createOverride',
    // Custom error handler to provide more context without the full payload
    customErrorContext: (payload) => ({
      description: payload?.description,
      entityTypes: payload?.entityTypes,
      dataTypes: payload?.dataTypes,
      overridesCount: payload?.overrides?.length || 0,
    }),
  },
)

export const fetchOverrideLookup = createTrackedAsyncThunk(
  'feeManagement/fetchOverrideLookup',
  async (queryString, { rejectWithValue }) => {
    try {
      // Override lookup/simulation: GET /overrides/lookup?<query>. The endpoint
      // expands a selected branch into its ancestry server-side, so this mirrors
      // how an order in that branch would resolve.
      const response = await API(`overrides/lookup?${queryString}`, API_VERSION.TWO).request(REQUEST_METHOD.GET)
      return response
    } catch (err) {
      return rejectWithValue(err?._error || err?.error || 'Failed to look up overrides')
    }
  },
  { errorSource: 'feeManagement.overrideLookup' },
)

export const fetchMappingData = createTrackedAsyncThunk(
  'feeManagement/fetchMappingData',
  async (entityTypes, { rejectWithValue }) => {
    if (!entityTypes || !entityTypes.length) {
      return { newData: {} }
    }

    // Always refetch the requested entity types. Fee Management is a long-lived
    // SPA session and entities (products, branches, panelists) can be created,
    // renamed, or deleted on other tabs while this cache sits untouched. Fetching
    // every time the data is requested keeps the validator in sync with the
    // backend; a short-circuit here is what let stale caches reject valid rows.
    try {
      const queryString = `entity_types=${entityTypes.join(',')}`
      const response = await API(`overrides/mapping-data?${queryString}`, API_VERSION.TWO).request(REQUEST_METHOD.GET)

      return { newData: response }
    } catch (err) {
      return rejectWithValue(err.errors?._error || 'Failed to fetch mapping data')
    }
  },
  { errorSource: 'feeManagement.mappingData' },
)

const feeManagementSlice = createSlice({
  name: 'feeManagement',
  initialState,
  reducers: {
    resetFeeManagement: () => initialState,
    setOverrideData: (state, action) => {
      state.overrideData.data = action.payload
    },
    setOverrideDataLoading: (state, action) => {
      state.overrideData.isLoading = action.payload
    },
    setOverrideDataError: (state, action) => {
      state.overrideData.error = action.payload
    },
    resetCreateOverrideState: (state) => {
      state.createOverride = {
        isLoading: false,
        error: null,
        success: false,
        createdSheetId: null,
        result: null,
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOverrideSheets.pending, (state) => {
        state.overrideSheets.isLoading = true
        state.overrideSheets.error = null
      })
      .addCase(fetchOverrideSheets.fulfilled, (state, action) => {
        state.overrideSheets.items = action.payload
        state.overrideSheets.isLoading = false
        state.overrideSheets.error = null
      })
      .addCase(fetchOverrideSheets.rejected, (state, action) => {
        state.overrideSheets.isLoading = false
        state.overrideSheets.error = action.payload
      })
      .addCase(createOverrideSheet.pending, (state) => {
        state.createOverride.isLoading = true
        state.createOverride.error = null
        state.createOverride.success = false
      })
      .addCase(createOverrideSheet.fulfilled, (state, action) => {
        state.createOverride.isLoading = false
        state.createOverride.success = true
        state.createOverride.createdSheetId = action.payload.sheet_id
        state.createOverride.result = {
          sheetId: action.payload.sheet_id,
          updatedCount: action.payload.updated_count || 0,
          insertedCount: action.payload.inserted_count || 0,
        }
      })
      .addCase(createOverrideSheet.rejected, (state, action) => {
        state.createOverride.isLoading = false
        state.createOverride.error = action.payload
        state.createOverride.success = false
      })
      .addCase(fetchOverrideLookup.pending, (state) => {
        state.overrideLookup.isLoading = true
        state.overrideLookup.error = null
      })
      .addCase(fetchOverrideLookup.fulfilled, (state, action) => {
        state.overrideLookup.isLoading = false
        state.overrideLookup.data = action.payload
        state.overrideLookup.error = null
      })
      .addCase(fetchOverrideLookup.rejected, (state, action) => {
        state.overrideLookup.isLoading = false
        state.overrideLookup.error = action.payload || 'Failed to look up overrides'
      })
      .addCase(fetchMappingData.pending, (state) => {
        state.mappingData.isLoading = true
        state.mappingData.error = null
      })
      .addCase(fetchMappingData.fulfilled, (state, action) => {
        const { newData } = action.payload

        // Replace each entity type present in the response instead of merging by
        // id. A merge can only ever add rows, so renamed or deleted entities keep
        // their stale values forever; full replacement lets those changes
        // propagate. Keys absent from the response are left untouched, so a subset
        // fetch (e.g. products only) doesn't wipe branches or panelists.
        if (newData.products !== undefined) {
          state.mappingData.data.products = newData.products
        }

        if (newData.panelists !== undefined) {
          state.mappingData.data.panelists = newData.panelists
        }

        if (newData.state_and_counties_map !== undefined) {
          state.mappingData.data.state_and_counties_map = newData.state_and_counties_map
        }

        if (newData.branches !== undefined) {
          state.mappingData.data.branches = newData.branches
        }

        state.mappingData.isLoading = false
      })
      .addCase(fetchMappingData.rejected, (state, action) => {
        state.mappingData.isLoading = false
        state.mappingData.error = action.payload
      })
      .addCase(updateOverrideSheet.pending, (state) => {
        state.updateOverride.isLoading = true
        state.updateOverride.error = null
      })
      .addCase(updateOverrideSheet.fulfilled, (state, action) => {
        state.updateOverride.isLoading = false
        state.updateOverride.error = null
        state.updateOverride.success = true
      })
      .addCase(updateOverrideSheet.rejected, (state, action) => {
        state.updateOverride.isLoading = false
        state.updateOverride.error = action.payload
        state.updateOverride.success = false
      })
      .addCase(fetchOverrideData.pending, (state) => {
        state.overrideData.isLoading = true
        state.overrideData.error = null
      })
      .addCase(fetchOverrideData.fulfilled, (state, action) => {
        state.overrideData.isLoading = false
        state.overrideData.data = action.payload
        state.overrideData.error = null
      })
      .addCase(fetchOverrideData.rejected, (state, action) => {
        state.overrideData.isLoading = false
        state.overrideData.error = action.payload
      })
      .addCase(updateOverrides.pending, (state) => {
        state.updateOverrides.isLoading = true
        state.updateOverrides.error = null
        state.updateOverrides.success = false
      })
      .addCase(updateOverrides.fulfilled, (state, action) => {
        state.updateOverrides.isLoading = false
        state.updateOverrides.error = null
        state.updateOverrides.success = true
        state.updateOverrides.result = {
          sheetId: action.payload.sheet_id,
          updatedCount: action.payload.updated_count || 0,
          insertedCount: action.payload.inserted_count || 0,
        }
      })
      .addCase(updateOverrides.rejected, (state, action) => {
        state.updateOverrides.isLoading = false
        state.updateOverrides.error = action.payload
        state.updateOverrides.success = false
      })
  },
})

export const {
  resetFeeManagement,
  setOverrideData,
  setOverrideDataLoading,
  setOverrideDataError,
  resetCreateOverrideState,
} = feeManagementSlice.actions

export default feeManagementSlice.reducer
